import HeatMapGrid from "react-heatmap-grid";
import React, {useEffect, useMemo} from "react";


const generateHeatmapData = (sensors) => {
    // Group sensors by rows and map their values
    if (!sensors || sensors.length === 0) {
        // Ensure data returns a structure compatible with the component
        return { xLabels: [""], yLabels: [], data: [[]] };
    }

    const xLabels = [""];

    const yLabels = sensors.map(sensor => "");

    const data = sensors.map((sensor) => ([
        {
            value: sensor.Value,
            status: sensor.Status,
            id: sensor.Id,
            unit: sensor.Meta.Units
        }
    ])).reverse();

    return { xLabels, yLabels, data };
};
const minTemp = 10;    // green
const maxTemp = 50;  // red
const getTemperatureColour = (value, unit) => {
    // Convert Fahrenheit to Celsius if necessary
    if (unit === "°F") {
        value = (value - 32) * 5 / 9; // Convert to Celsius
    }

    const clamped = Math.max(minTemp, Math.min(maxTemp, value));

    if (value <= minTemp) {
        return '#00FF00';
    }

    if (value >= maxTemp) {
        return '#FF0000';
    }

    const gradientRange = maxTemp - minTemp; // 40 degrees
    const percent = (clamped - minTemp) / gradientRange; // 0.0 to 1.0

    let r, g;
    const splitPoint = 0.5;

    if (percent <= splitPoint) {
        r = Math.floor(255 * (percent / splitPoint));
        g = 255;
    } else {
        r = 255;

        g = Math.floor(255 * (1 - (percent - splitPoint) / splitPoint));
    }

    const redHex = r.toString(16).padStart(2, '0');
    const greenHex = g.toString(16).padStart(2, '0');

    return `#${redHex}${greenHex}00`;
};

const getTextColor = (hexColor) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const hex = hexColor.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
    });

    // 2. Extract R, G, B hex pairs
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
        console.error(`Invalid hex color: ${hexColor}`);
        return "black";
    }


    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;


    return brightness > 125 ? "black" : "white";
};

const SiloHeatMap = ({ sensors,onSensorClick,selectedSensors,groupName }) => {

    // Data is now transposed here
    const { xLabels, yLabels, data } = generateHeatmapData(sensors);
    const safeSelectedSensors = Array.isArray(selectedSensors) ? selectedSensors : [];


    const Y_LABEL_WIDTH = 20;
    const TOTAL_WIDTH = Y_LABEL_WIDTH + 60;


    return (
        <div style={{width: `${TOTAL_WIDTH}px`, fontSize: "12px", height: "100%", overflowY: "auto", alignItems: "center"}}>
            <div style={{paddingLeft:  `${Y_LABEL_WIDTH - 5}px`}}>
                <h4 style={{margin: "10px 0 10px 0", fontSize: '14px', textAlign: 'left'}}>
                    {groupName || 'Channel'}
                </h4>
            </div>
            <div style={{display: "flex", justifyContent: "flex-start", padding: "0px 0px 0px"}}>

                <HeatMapGrid
                    xLabels={xLabels}
                    yLabels={yLabels}
                    data={data} // Contains objects now
                    cellHeight="1.5rem"
                    yLabelWidth={Y_LABEL_WIDTH}
                    xLabelsPos="bottom"
                    cellStyle={(_x, _y) => {
                        const { value, status, id, unit } = _y;

                        const bgColor = getTemperatureColour(value, unit);
                        const textColor = getTextColor(bgColor);
                        const isSelected = safeSelectedSensors.includes(id);

                        if (status === "WARNING") {
                            return {
                                background: "rgb(255, 0, 0)",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: "bold",
                                 borderRadius: '4px', // Softened corners
                                border: isSelected ? "2px solid #A9CC13" : "none", // Highlight is border
                                boxShadow: isSelected ? "inset 0 0 10px rgba(0, 123, 255, 0.5), 0 2px 5px rgba(0, 0, 0, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
                                   transition: "all 0.3s ease-in-out",
                            };
                        }

                        if (status === "Alarm") {
                            return {
                                background: "rgb(0, 0, 0)",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: "bold",
                                 borderRadius: '4px', // Softened corners
                                border: isSelected ? "2px solid #A9CC13" : "none", // Highlight is border
                                boxShadow: isSelected ? "inset 0 0 10px rgba(0, 123, 255, 0.5), 0 2px 5px rgba(0, 0, 0, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
                                   transition: "all 0.3s ease-in-out",
                            };
                        }

                        return {
                            background: bgColor,
                            color: textColor,
                            fontSize: "12px",
                            cursor: "pointer",
                            borderRadius: '4px', // Softened corners
                            border: isSelected ? "2px solid #A9CC13" : "none", // Highlight is border
                            boxShadow: isSelected ? "inset 0 0 10px rgba(0, 123, 255, 0.5), 0 2px 5px rgba(0, 0, 0, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.1)", // Subtle shadow
                            transform: isSelected ? 'scale(1.02)': 'scale(1.00)',
                            transition: "all 0.3s ease-in-out",
                        };
                    }}

                    // --- FIX: Destructure properties from the cell object directly (3rd argument) ---
                    cellRender={(_x, _y) => (
                        <div title={`Value: ${_x.value} ${_x.unit}`}>
                            {_x.value}
                            {_x.unit === "C" ? <span>&deg;C</span> : _x.unit}
                            {_x.status === "WARNING" && " ⚠"}
                        </div>
                    )}

                    onClick={(_x, y) => {
                        // The clicked sensor object is at data[row=y][column=0]
                        const clickedSensor = data[y][0];
                        onSensorClick(clickedSensor.id);
                    }}
                    xLabelsStyle={_index => ({ color: "#777", fontSize: ".65rem", textAlign: 'center' })}
                    yLabelsStyle={() => ({ fontSize: ".65rem", textTransform: "none", color: "#777", paddingRight: '10px',textAlign: 'center' })}
                />
            </div>
        </div>
    );
};

export default SiloHeatMap;
